import 'intersection-observer';

import React from 'react';
import { hydrate } from 'react-dom';

import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  createHttpLink,
} from '@apollo/client';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { RetryLink } from '@apollo/client/link/retry';
import { loadableReady } from '@loadable/component';
import { init as SentryInit } from '@sentry/browser';
import { sha256 } from 'js-sha256';

import AppWithProviders from '../components/AppWithProviders/AppWithProviders';
import { createAuth } from '../utils/authCreator';
import { cacheConfig } from './cache';
import clientConfig from './config';
import { fetchWithTokenRefresh } from './fetchWithTokenRefresh';
import i18n from './i18nClient';
import { manageAnalyticsCookie } from './manageAnalyticsCookie';
import { initRum } from './rum';

initRum();

const auth = createAuth(document);

manageAnalyticsCookie(auth);

const link = ApolloLink.from([
  createPersistedQueryLink({ sha256, useGETForHashedQueries: true }),
  new RetryLink({
    delay: {
      initial: 300,
      max: Infinity,
      jitter: true,
    },
    attempts: {
      max: 5,
      retryIf: error => !!error,
    },
  }),
  createHttpLink({
    uri: clientConfig.graphqlUrl,
    fetch: (input, init) => {
      if (window.__PageMetaContext.isPreview) {
        init = { ...init, credentials: 'include' };
      }

      if (init?.credentials === 'include') {
        return fetchWithTokenRefresh(auth, input, init);
      }

      return fetch(input, init);
    },
  }),
]);

const client = new ApolloClient({
  name: `JG.Pages.UI (client side, ${window.__PageMetaContext.pageType})`,
  version: window.__RELEASE_VERSION,
  link,
  cache: new InMemoryCache(cacheConfig).restore(window.__APOLLO_STATE__),
  assumeImmutableResults: true,
});

if (
  clientConfig.sentry &&
  clientConfig.sentry.dsn &&
  clientConfig.sentry.config
) {
  /* eslint-disable no-underscore-dangle */
  const revision = window.__RELEASE_VERSION;
  const release = revision && revision.toString();

  SentryInit({
    dsn: clientConfig.sentry.dsn,
    release,
    environment: clientConfig.sentry.config.environment,
    whitelistUrls: [/justgiving\.com/, /jg-cdn\.com/],
    sampleRate: 0.1,
    ignoreErrors: [
      /Circuit open for request/,
      /Request timeout after/,
      /Network error: Failed to fetch/,
      /Non-Error promise rejection/,
    ],
  });
}

loadableReady(() => {
  hydrate(
    <AppWithProviders
      pageMetaContextProviderValue={window.__PageMetaContext}
      apolloProviderClient={client}
      i18n={i18n}
      auth={auth}
      flagOverrides={window.__FLAG_OVERRIDES}
    />,
    document.getElementById('content')!,
  );
});
